import { Table, Typography, Pagination, Button, Dropdown, Menu } from "antd";
import { PlusOutlined, MinusOutlined, MoreOutlined } from "@ant-design/icons";
import { useState } from "react";
import styles from "./custom-table.module.css";

const CustomTable = ({
  dataSource,
  columns,
  rowKey = "key",
  pagination = true,
  collapsible = false,
  showMoreIcon,
  menuItems,
  currentPage = 1,
  pageSize = 10,
  total = 0,
  handlePageChange,
  handleExpand,
  showSizeChanger = false,
  pageSizeOptions = ["10", "25", "50", "100"],
  showTotalText = true,
  showTotal = (total, range) =>
    `Showing ${range[0]}-${range[1]} of ${total} entries`,
  renderCollapseContent,
  onChildClick,
  cellHeight = "44px",
  headerBackgroundColor = "#EFF1F5",
  headerColor = "#222",
  loading = false,
  ...rest
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const onHandleExpand = (expanded, record) => {
    handleExpand(expanded, record);
    if (expanded) {
      setExpandedRowKeys([record.key]);
    } else {
      setExpandedRowKeys([]);
    }
  };

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            backgroundColor: headerBackgroundColor,
            padding: "12px 24px",
            height: "44px",
            textAlign: props?.align || "left",
            color: headerColor,
            fontSize: "12px",
          }}
        />
      ),
    },
    body: {
      cell: (props) => (
        <td
          {...props}
          style={{
            backgroundColor: "#fff",
            padding: "12px 24px",
            height: cellHeight,
            textAlign: props?.align || "left",
            color: "var(--text-black-color)",
            fontSize: "14px",
            fontWeight: "500",
          }}
        />
      ),
    },
  };

  const enhancedColumns = [
    ...columns?.map((col) => ({
      ...col,
      onHeaderCell: () => ({
        align: col?.align,
      }),
      onCell: () => ({
        align: col?.align,
      }),
    })),
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: 50,
      align: "center",
      render: (_, record) => {
        const key = record[rowKey];
        const isParent = !!record.children;

        if (showMoreIcon && !isParent) {
          return (
            <Dropdown
              overlay={<Menu items={menuItems?.(record) || []} />}
              trigger={["click"]}
            >
              <Button
                type="text"
                icon={<MoreOutlined />}
                className="action-button" // Add class to identify actions
              />
            </Dropdown>
          );
        }

        return null;
      },
    },
  ];

  return (
    <div>
      <Table
        dataSource={dataSource}
        columns={enhancedColumns}
        rowKey="key"
        pagination={false}
        components={components}
        scroll={{ x: "max-content" }}
        loading={loading}
        expandable={
          collapsible
            ? {
                fixed: "right",
                onExpand: (expanded, record) =>
                  onHandleExpand(expanded, record),
                expandedRowRender: renderCollapseContent,
                expandedRowKeys,
                expandIcon: ({ expanded, onExpand, record }) => (
                  <Button
                    type="text"
                    className="action-button"
                    icon={
                      expanded ? (
                        <MinusOutlined
                          style={{ color: "var(--primary-color)" }}
                        />
                      ) : (
                        <PlusOutlined
                          style={{ color: "var(--primary-color)" }}
                        />
                      )
                    }
                    onClick={(event) => {
                      event.stopPropagation();
                      onExpand(record, event);
                    }}
                  />
                ),
              }
            : undefined
        }
        {...rest}
      />

      {pagination !== false && (
        <div
          style={{
            marginTop: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "5px",
          }}
        >
          {showTotalText ? (
            <Typography.Text className={styles.show_total_text}>
              {showTotal(total, [
                (currentPage - 1) * pageSize + 1,
                Math.min(currentPage * pageSize, total),
              ])}
            </Typography.Text>
          ) : null}
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={total}
            onChange={handlePageChange}
            showSizeChanger={showSizeChanger}
            pageSizeOptions={pageSizeOptions}
          />
        </div>
      )}
    </div>
  );
};

export default CustomTable;
